'use client'

export type GppSignalStatus = 'not ready' | 'ready'

export type GppPingData = {
    applicableSections: number[]
    cmpStatus: string
    gppString: string
    gppVersion: string
    signalStatus: GppSignalStatus
}

export type GppEvent = {
    eventName: string
    listenerId: number
    pingData: GppPingData
}

export type GppCallback = (data: GppPingData | GppEvent, success: boolean) => void
export type GppApi = (command: string, callback: GppCallback, parameter?: unknown, version?: string) => void

type GppTarget = Pick<Window, '__gpp'>

const readinessPromises = new WeakMap<object, Promise<GppPingData>>()

function pingDataFromCallback(data: GppPingData | GppEvent): GppPingData {
    return 'pingData' in data ? data.pingData : data
}

export function resolveGppReady(api: GppApi): Promise<GppPingData> {
    return new Promise((resolve) => {
        let listenerId: number | undefined
        let resolved = false

        const resolveIfReady = (data: GppPingData | GppEvent, success: boolean) => {
            if (!success || resolved) {
                return
            }

            if ('listenerId' in data) {
                listenerId = data.listenerId
            }

            const pingData = pingDataFromCallback(data)
            if (pingData.signalStatus !== 'ready') {
                return
            }

            resolved = true
            if (listenerId !== undefined) {
                api('removeEventListener', () => undefined, listenerId)
            }
            resolve(pingData)
        }

        api('ping', (data, success) => {
            if (success && pingDataFromCallback(data).signalStatus === 'ready') {
                resolveIfReady(data, success)
                return
            }
            api('addEventListener', resolveIfReady)
        })
    })
}

export function waitForGppReady(target: GppTarget = window): Promise<GppPingData> {
    const cachedReadiness = readinessPromises.get(target)
    if (cachedReadiness) {
        return cachedReadiness
    }

    const readiness = target.__gpp ? resolveGppReady(target.__gpp) : new Promise<GppPingData>(() => undefined)
    readinessPromises.set(target, readiness)
    return readiness
}
